import { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Loader2, SlidersHorizontal, Clock } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { ProductCard } from '@/components/products/ProductCard';
import { useNewArrivals } from '@/hooks/useApi.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const NewArrivalsPage = () => {
  const [sortBy, setSortBy] = useState('recent');
  const [days, setDays] = useState(30);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data: newArrivalsData, isLoading, error } = useNewArrivals(page, pageSize, days);
  const newArrivals = newArrivalsData?.content || [];
  const hasMore = newArrivalsData?.page?.number < (newArrivalsData?.page?.totalPages - 1);
  const totalItems = newArrivalsData?.page?.totalElements || 0;

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    setPage(0); // Reset to first page when sorting changes
  };

  const handleDaysChange = (value: string) => {
    setDays(parseInt(value));
    setPage(0); // Reset to first page when time filter changes
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="h-6 w-6 text-primary" />
            <span className="text-primary font-medium text-sm uppercase tracking-wider">
              Just Dropped
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            New <span className="text-gradient-rainbow">Arrivals</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Discover the latest fashion trends and fresh styles that just landed in our collection
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-8 p-4 bg-card rounded-lg border"
        >
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              Showing {newArrivals.length} of {totalItems} new arrivals
            </span>
          </div>
          
          <div className="flex gap-3">
            <Select value={days.toString()} onValueChange={handleDaysChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 Days</SelectItem>
                <SelectItem value="14">Last 2 Weeks</SelectItem>
                <SelectItem value="30">Last Month</SelectItem>
                <SelectItem value="60">Last 2 Months</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={handleSortChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Most Recent</SelectItem>
                <SelectItem value="popular">Most Popular</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="rating">Highest Rated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Products Grid */}
        {isLoading && page === 0 ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <span className="text-lg">Loading new arrivals...</span>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="max-w-md mx-auto">
              <p className="text-muted-foreground mb-4">Failed to load new arrivals</p>
              <Button onClick={() => window.location.reload()}>Try Again</Button>
            </div>
          </div>
        ) : newArrivals.length === 0 ? (
          <div className="text-center py-20">
            <div className="max-w-md mx-auto">
              <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No New Arrivals</h3>
              <p className="text-muted-foreground mb-4">
                No new products found in the selected time period. Try expanding the time range!
              </p>
            </div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            {/* Products Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {newArrivals.map((product, index) => (
                <motion.div
                  key={product.productId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-center pt-8"
              >
                <Button
                  onClick={handleLoadMore}
                  variant="outline"
                  size="lg"
                  disabled={isLoading}
                  className="min-w-[200px]"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Loading...
                    </>
                  ) : (
                    <>
                      Load More Arrivals
                      <Sparkles className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Time-based Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 pt-12 border-t border-border"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-2">Fresh Fashion, Daily</h3>
            <p className="text-muted-foreground">
              We add new styles to our collection every week
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-2xl md:text-3xl font-bold text-primary mb-2">
                {days === 7 ? '15+' : days === 14 ? '30+' : days === 30 ? '50+' : '100+'}
              </div>
              <p className="text-muted-foreground">New Products Added</p>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-primary mb-2">24/7</div>
              <p className="text-muted-foreground">Fresh Collection Updates</p>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-primary mb-2">4.8★</div>
              <p className="text-muted-foreground">Average Rating</p>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default NewArrivalsPage;